import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Divider,
  Chip,
  Menu,
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Add as AddIcon,
} from "@material-ui/icons";
import { createEventAttribute } from "../../../../../data-store/actions/events-actions";
import EmailBlockPreview from "./EmailBlockPreview";
import { uid, BLOQUES_GENERICOS, bloqueGenericoPorDefecto, EditorBloqueGenerico } from "./genericBlockShared";

// Editor de bloques del correo de confirmación de inscripción. Por defecto
// (evento sin diseño guardado) se precarga localmente la misma estructura
// fija que usa hoy plantillaConfirmacionInscripcion (functions/modules/
// mailingfunctions.js), migrando también los bloques de texto/imagen/link
// que ya se hubieran agregado con el editor viejo (adicionalesCorreo). No se
// escribe nada a Firebase hasta presionar "Guardar diseño": mientras tanto
// el evento sigue usando la plantilla fija de siempre sin ningún cambio.

// Bloques "de datos" de Dashport: su contenido lo arma el backend con datos
// reales del atleta/evento al momento de enviar, aquí solo se puede mover,
// quitar o volver a agregar. Deben coincidir 1 a 1 con los `case` de
// functions/modules/emailBlocks/renderConfirmationEmail.js.
const ESTRUCTURA_DASHPORT = [
  { type: "organizerHeader", label: "Encabezado", descripcion: "Logo del organizador (o Dashport) con el color de fondo del evento." },
  { type: "eventTitle", label: "Título de bienvenida", descripcion: "Saludo + nombre del atleta + nombre del evento." },
  { type: "eventLogo", label: "Logo del evento", descripcion: "Logo del evento en tamaño grande." },
  { type: "confirmationLink", label: "Link de confirmación", descripcion: "Botón para descargar la confirmación de inscripción y el link al detalle del evento." },
  { type: "athleteData", label: "Datos del atleta", descripcion: "Tabla con número, distancia, rama y categoría." },
  { type: "orderDetail", label: "Detalle de la compra", descripcion: "La inscripción y cualquier producto adicional comprado en el checkout — va en todos los correos de confirmación." },
  { type: "paymentInfo", label: "Información de pago", descripcion: "Ficha de pago pendiente y/o detalle del pago con tarjeta, si aplica." },
  { type: "footer", label: "Pie de página", descripcion: "Nombre y link del organizador." },
];
const ESTRUCTURA_DASHPORT_TYPES = ESTRUCTURA_DASHPORT.map((b) => b.type);

// Convierte los bloques del editor viejo (inscripcionesConfiguracion/
// adicionalesCorreo) a los tipos genéricos de este editor, para no perder lo
// que un evento ya hubiera personalizado antes de que existiera este editor.
function migrarAdicionalesCorreo(adicionalesCorreo) {
  const items = Array.isArray(adicionalesCorreo)
    ? adicionalesCorreo
    : Object.values(adicionalesCorreo || {});
  return items.filter(Boolean).map((item) => {
    if (item.type === "image") {
      return { id: uid(), type: "image", src: item.value, alt: item.name || "", width: 212, align: "center" };
    }
    if (item.type === "link") {
      return { id: uid(), type: "link", value: item.value };
    }
    const esTitulo = item.htmlLabel === "h1" || item.htmlLabel === "h3" || item.htmlLabel === "h4";
    return esTitulo
      ? { id: uid(), type: "heading", text: item.value, size: item.htmlLabel === "h1" ? 24 : 18, align: "left" }
      : { id: uid(), type: "text", text: item.value, size: 14, color: "#111827", align: "left" };
  });
}

function bloquesPorDefecto(adicionalesCorreo) {
  const base = ESTRUCTURA_DASHPORT.map((b) => ({ id: uid(), type: b.type }));
  const migrados = migrarAdicionalesCorreo(adicionalesCorreo);
  // los bloques que ya existían con el editor viejo vivían justo antes de la
  // ficha de pago (mismo lugar que "datosAdicionalesCorreo" en la plantilla
  // fija original).
  const idxPago = base.findIndex((b) => b.type === "paymentInfo");
  return [...base.slice(0, idxPago), ...migrados, ...base.slice(idxPago)];
}

export default function EmailBlockBuilder() {
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const registryConfiguration = useSelector((state) => state.registry.registryConfiguration);
  const eventoIndex = "evento_" + eventDetail.iDEvento;

  const [blocks, setBlocks] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [anchorAgregar, setAnchorAgregar] = useState(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    if (registryConfiguration === undefined) return; // sigue cargando desde Firebase
    const guardado = registryConfiguration?.emailConfirmacion?.blocks;
    if (Array.isArray(guardado) && guardado.length > 0) {
      setBlocks(guardado.map((b) => ({ id: uid(), ...b })));
    } else {
      setBlocks(bloquesPorDefecto(registryConfiguration?.adicionalesCorreo));
    }
    initializedRef.current = true;
  }, [registryConfiguration]);

  if (blocks === null) {
    return (
      <Paper style={{ padding: 20, marginTop: 20 }}>
        <Typography variant="body2">Cargando diseño del correo…</Typography>
      </Paper>
    );
  }

  const tiposUsados = new Set(blocks.map((b) => b.type));
  const estructuraDisponible = ESTRUCTURA_DASHPORT.filter((b) => !tiposUsados.has(b.type));

  function moverBloque(idx, dir) {
    setBlocks((prev) => {
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  function quitarBloque(idx) {
    setBlocks((prev) => prev.filter((_, i) => i !== idx));
  }

  function actualizarBloque(idx, patch) {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function agregarBloque(type) {
    const nuevo = ESTRUCTURA_DASHPORT_TYPES.includes(type)
      ? { id: uid(), type }
      : bloqueGenericoPorDefecto(type);
    setBlocks((prev) => [...prev, nuevo]);
    setAnchorAgregar(null);
  }

  function restaurarPorDefecto() {
    setBlocks(bloquesPorDefecto(registryConfiguration?.adicionalesCorreo));
    setMensaje("");
  }

  function guardarDiseno() {
    setGuardando(true);
    const payload = blocks.map(({ id, ...rest }) => rest);
    dispatch(
      createEventAttribute(eventoIndex, "inscripcionesConfiguracion/emailConfirmacion/blocks", payload)
    ).finally(() => {
      setGuardando(false);
      setMensaje("Diseño guardado. Los próximos correos de confirmación lo usarán.");
    });
  }

  function quitarDisenoPersonalizado() {
    setGuardando(true);
    dispatch(createEventAttribute(eventoIndex, "inscripcionesConfiguracion/emailConfirmacion", null)).finally(() => {
      setGuardando(false);
      initializedRef.current = false;
      setBlocks(null);
      setMensaje("Se quitó el diseño personalizado, el correo vuelve a la estructura fija de siempre.");
    });
  }

  return (
    <Box mt={2}>
      <Paper style={{ padding: 20 }}>
        <Typography variant="h5" gutterBottom>
          Diseño del correo de confirmación
        </Typography>
        <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 16 }}>
          Por defecto se usa la estructura de siempre. Puedes quitar, agregar o reordenar
          cada parte con las flechas, y guardar tu propio diseño para este evento. A la
          derecha puedes ver cómo va quedando (vista previa aproximada, con un atleta de ejemplo).
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {blocks.map((b, idx) => {
              const esDashport = ESTRUCTURA_DASHPORT_TYPES.includes(b.type);
              const infoDashport = esDashport && ESTRUCTURA_DASHPORT.find((d) => d.type === b.type);
              const infoGenerico = !esDashport && BLOQUES_GENERICOS.find((d) => d.type === b.type);
              return (
                <Paper key={b.id} variant="outlined" style={{ padding: 12, marginBottom: 10 }}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Chip
                      size="small"
                      label={infoDashport ? infoDashport.label : infoGenerico?.label || b.type}
                      color={esDashport ? "primary" : "default"}
                    />
                    <Box flexGrow={1} />
                    <IconButton size="small" onClick={() => moverBloque(idx, "up")} disabled={idx === 0}>
                      <UpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moverBloque(idx, "down")} disabled={idx === blocks.length - 1}>
                      <DownIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => quitarBloque(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {esDashport && (
                    <Typography variant="caption" style={{ display: "block", marginTop: 6, opacity: 0.7 }}>
                      {infoDashport.descripcion} Este contenido lo arma Dashport con los datos
                      reales de cada atleta al momento de enviar, por eso no tiene texto propio
                      para editar aquí — solo puedes moverlo o quitarlo.
                    </Typography>
                  )}
                  {!esDashport && (
                    <Box mt={1}>
                      <EditorBloqueGenerico
                        block={b}
                        eventoIndex={eventoIndex}
                        onChange={(patch) => actualizarBloque(idx, patch)}
                      />
                    </Box>
                  )}
                </Paper>
              );
            })}

            <Box mt={2} mb={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setAnchorAgregar(e.currentTarget)}
              >
                Agregar bloque
              </Button>
              <Menu anchorEl={anchorAgregar} open={!!anchorAgregar} onClose={() => setAnchorAgregar(null)}>
                {estructuraDisponible.length > 0 && (
                  <Typography variant="caption" style={{ padding: "6px 16px", display: "block", opacity: 0.6 }}>
                    Estructura de Dashport
                  </Typography>
                )}
                {estructuraDisponible.map((b) => (
                  <MenuItem key={b.type} onClick={() => agregarBloque(b.type)}>
                    {b.label}
                  </MenuItem>
                ))}
                <Divider />
                <Typography variant="caption" style={{ padding: "6px 16px", display: "block", opacity: 0.6 }}>
                  Bloque personalizado
                </Typography>
                {BLOQUES_GENERICOS.map((b) => (
                  <MenuItem key={b.type} onClick={() => agregarBloque(b.type)}>
                    {b.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Divider style={{ margin: "16px 0" }} />

            <Box display="flex" style={{ gap: 12 }} flexWrap="wrap" alignItems="center">
              <Button variant="contained" color="primary" onClick={guardarDiseno} disabled={guardando}>
                Guardar diseño
              </Button>
              <Button variant="outlined" onClick={restaurarPorDefecto} disabled={guardando}>
                Restaurar estructura por defecto
              </Button>
              {registryConfiguration?.emailConfirmacion?.blocks && (
                <Button variant="outlined" color="secondary" onClick={quitarDisenoPersonalizado} disabled={guardando}>
                  Quitar diseño personalizado
                </Button>
              )}
              {mensaje && (
                <Typography variant="body2" style={{ opacity: 0.75 }}>
                  {mensaje}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box style={{ position: "sticky", top: 20 }}>
              <Typography variant="subtitle2" gutterBottom>
                Vista previa (aproximada, con un atleta de ejemplo)
              </Typography>
              <EmailBlockPreview blocks={blocks} eventDetail={eventDetail} />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
